import { isNumber } from 'radash';
import { v4 as uuidv4 } from 'uuid';
import {
  getNewTrimFromEndDeletesForComposite,
  getNewTrimFromStartDeletesForComposite
} from '../../ClipSpeakersTimeline/ClipSpeakersTimelineTrim/utlis';
import { UpdateClipAction, UpdateClipType } from './types';
import { CLIP_UPDATE_TYPE } from './constants';
import {
  addCompositeSegmentDelete,
  getCompositeSegmentById,
  getAllCompositeSegments,
  splitSegment,
  addSegmentDeletes,
  addSegmentRestore,
  getAllSegments
} from './compositeUtils';
import {
  addNewSceneAtFrame,
  addNewSceneAtRange,
  createNewScene,
  createNewSceneAtRange,
  deleteScene,
  getDefaultSceneData,
  toggleVideoVisibilityFromScene,
  trimScenes
} from './sceneUtils';

import { updateCaptionPositions, updateCaptions, getDefaultCaptionStyles, updateGlobalCaptions } from './captionUtils';
import { updateCurrentClipUpdatedAt } from './updatedAtUtils';
import { shouldAddToHistory } from './historyUtils';
import { injectSegmentIdInUpdateClipPayload } from './splitUtils';
import {
  upsertSegmentTransition,
  removeSegmentTransition,
  updateTransitionsWhenSegmentAdded,
  updateTransitionsWhenSegmentRemoved,
  removeTransitionsForSegment
} from './segmentTransitionUtils';
import { applyCorrectAll, applyWordEdit, applyWordHide, applyWordShow } from './transcriptUtils';
import { isChapterAffectingAction } from './chapterUpdateUtils';
import { reorderSegment } from './dragUtils';
import { syncAssetMetadataFromSegments } from './syncAssetMetadataFromSegments';
import { buildHookElementTracks, isolateHookSelectionSegment } from './createHookUtils';
import {
  findAndReplaceElementTrackItemInPlayerConfig,
  updateElementTransition
} from '../clipUpdateDataUtils/transitionUtils';
import { getSegmentAtCurrentFrame } from '../timeUtils';
import { SegmentType } from '@/App/remotion/MainPlayer/types/Segment/Type';
import { allClips, currentClip, isSplitsInTimelineEnabled, templateLoader, trackClipUpdate } from '@/stores/clip';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { debounce, roundToNDecimalPlaces } from '@/libs/utils';
import { mergeDeletes } from '@/components/molecules/Transcript/EditTranscriptMenu/utils';
import { generateThumbnail } from '@/libs/thumbnail';
import { updateHistory } from '@/stores/clipHistory';
import { getLayoutAvailabilityStatus } from '@/context/ClipsContext/ClipsContextUtils';
import { getClipFPS, getClipReadOnlyPlayerConfig, getClipVersion } from '@/libs/clipUtils';
import { updateClip, updateTranslatedCaption } from '@/libs/clipContentUtil';
import { CaptionLineConfig, CaptionStyle, Clip, ClipDeletes } from '@/domains/asset';
import { addPendingUpdate } from '@/stores/pendingEditsStore';
import { Scene } from '@/App/remotion/MainPlayer/types/Scene';
import { DEFAULT_FONT_SIZE } from '@/App/remotion/libs/caption/styles';
import { getCaptionPreset } from '@/App/remotion/libs/caption/caption-presets/utils';
import { createVideoSegmentWithMetaData } from '@/App/remotion/MainPlayer/utils/playerConfig/videoSegment/videoSegmentWithMetadata';
import { TrackType } from '@/App/remotion/MainPlayer/types/Track/Type';
import { SegmentsTrack } from '@/App/remotion/MainPlayer/types/Track/SegmentsTrack';
import { getPlayerConfigElementById } from '@/libs/playerConfigUtils';
import { ImageElement } from '@/App/remotion/MainPlayer/types/Element/ImageElement';
import { VideoElement } from '@/App/remotion/MainPlayer/types/Element/VideoElement';
import { getClipTotalDurationInFrames } from '@/App/remotion/MainPlayer/utils/configUtils';
import { approxEquals } from '@/libs/compare';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import {
  injectSegmentMetaDataInClip,
  injectSegmentMetaDataInCompositeSegment
} from '@/App/remotion/MainPlayer/utils/playerConfig/configWithSplits';
import { getPlayerConfig } from '@/App/remotion/MainPlayer/utils/playerConfig/config';

const generateThumbnailTriggers: UpdateClipAction['actionType'][] = [
  CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
  CLIP_UPDATE_TYPE.ADD_COMPOSITE_SCENE,
  CLIP_UPDATE_TYPE.ADD_COMPOSITE_SCENE_RANGE,
  CLIP_UPDATE_TYPE.REMOVE_COMPOSITE_SCENE,
  CLIP_UPDATE_TYPE.UPDATE_COMPOSITE_SCENE,
  CLIP_UPDATE_TYPE.UPDATE_CLIP_SIZE,
  CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
  CLIP_UPDATE_TYPE.UPDATE_CLIP_FIT_MODE,
  CLIP_UPDATE_TYPE.UPDATE_CLIP_BORDERS_VISIBILITY
];
const shouldGenerateThumbnail = (actionType: UpdateClipAction['actionType']) => {
  return generateThumbnailTriggers.includes(actionType);
};
const debouncedUpdateClipApi = debounce(
  ({ clip, onSuccess, version }: { clip: Clip; onSuccess: (updatedClip: Clip) => void; version: number }) => {
    addPendingUpdate({ clipId: clip.id, updatePromise: updateClip(clip).then(onSuccess), version });
  }
);

const debouncedUpdateTranslatedCaption = debounce(
  ({ clip, onSuccess, version }: { clip: Clip; onSuccess: (updatedClip: Clip) => void; version: number }) => {
    addPendingUpdate({ clipId: clip.id, updatePromise: updateTranslatedCaption(clip).then(onSuccess), version });
  }
);

const actionForTranslatedCaptionChange: UpdateClipAction['actionType'][] = [
  CLIP_UPDATE_TYPE.EDIT_WORD,
  CLIP_UPDATE_TYPE.HIDE_WORD,
  CLIP_UPDATE_TYPE.SHOW_WORD,
  CLIP_UPDATE_TYPE.CORRECT_ALL
];

// This delay is added to ensure the dom elements used for image generation are not removed before the image is generated.
const delayForThumbnailGeneration = 1000;

const debouncedGenerateThumbnail = debounce((clipId: string) => {
  generateThumbnail(clipId);
}, delayForThumbnailGeneration);

/**
 * Updates the clip data based on the provided update action.
 * Handles various update types such as adding/removing segment tracks,
 * updating composite segment deletes, and updating scene crop positions.
 * Also manages history, triggers thumbnail generation, and updates captions as needed.
 *
 * @param {UpdateClipType} updateClipPayload - The payload describing the update action and clip id.
 * @param {boolean} [shouldSkipSave=false] - If true, skips saving the updated clip to the backend or other stores. Used when are using some drag interactions like deleting words, deleting composite segment deletes, etc.
 * @returns {Clip} The updated clip object.
 */
/**
 * @example
 * updateClipData({
 *   clipId: 'clip-123',
 *   actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_SIZE,
 *   size: 'PORTRAIT'
 * });
 *
 * @throws {Error} When action type is not implemented
 * @returns {Clip} The updated clip object
 */
export const updateClipDataWithSplits = (
  updateClipPayloadWithoutCompositeId: UpdateClipType,
  options: {
    skipStoreAndBackendUpdate?: boolean;
    skipBackendUpdate?: boolean;
    currentClipData?: Clip | null;
    skipPostProcessing?: boolean;
  } = {
    skipStoreAndBackendUpdate: false,
    skipBackendUpdate: false,
    currentClipData: null,
    skipPostProcessing: false
  }
) => {
  const { skipStoreAndBackendUpdate = false, skipBackendUpdate = false, skipPostProcessing = false } = options;
  const { clipId } = updateClipPayloadWithoutCompositeId;
  const currentClipData = currentClip.getSnapshot();
  const oldClip = options.currentClipData || currentClipData[clipId];
  const newClip = injectSegmentMetaDataInCompositeSegment(oldClip);
  const updateClipPayload = injectSegmentIdInUpdateClipPayload(updateClipPayloadWithoutCompositeId as any, oldClip);
  const { actionType } = updateClipPayload;

  const eventsToDispatch: Record<string, boolean | Record<string, any>> = {};
  const addToHistory = shouldAddToHistory();
  const isSegmentTransitionsEnabled = featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Segment_Transitions];

  newClip.asset_metadata.is_edited = true;
  newClip.asset_metadata.isLayoutProcessing =
    getLayoutAvailabilityStatus(newClip.asset_metadata.layout, newClip.layout_status, true) === 'PROCESSING';

  switch (actionType) {
    case CLIP_UPDATE_TYPE.ADD_SEGMENT_TRACK: {
      const { id: segmentId, url, duration, afterSegmentId, insertFrame } = updateClipPayload;
      const playerConfig = newClip.asset_metadata.player_config;
      const totalDurationInFrames = getClipTotalDurationInFrames(newClip);
      const segmentTracks = playerConfig?.playerConfig.tracks.find(
        track => track.type === TrackType.SEGMENTS
      ) as SegmentsTrack;
      if (!segmentTracks?.segments) {
        throw new Error('Segment tracks not found');
      }
      if (segmentId === 'intro') {
        const introSegment = createVideoSegmentWithMetaData(newClip, 0, duration, url, 'intro');
        segmentTracks.segments.unshift(introSegment);
        if (isSegmentTransitionsEnabled) {
          updateTransitionsWhenSegmentAdded(newClip, 'fromSegmentId', 'intro');
        }
        newClip.asset_metadata.intro = url;
        newClip.asset_metadata.introDuration = duration;
      } else if (segmentId === 'outro') {
        const outroSegment = createVideoSegmentWithMetaData(
          newClip,
          totalDurationInFrames || 0,
          duration,
          url,
          'outro'
        );
        segmentTracks?.segments.push(outroSegment);
        if (isSegmentTransitionsEnabled) {
          updateTransitionsWhenSegmentAdded(newClip, 'toSegmentId', 'outro');
        }
        newClip.asset_metadata.outro = url;
        newClip.asset_metadata.outroDuration = duration;
      } else if (afterSegmentId && insertFrame !== undefined) {
        const newSegment = createVideoSegmentWithMetaData(newClip, insertFrame, duration, url);
        const segmentIndex = segmentTracks.segments.findIndex(seg => seg.id === afterSegmentId);
        if (segmentIndex !== -1) {
          const nextSegment = segmentTracks.segments[segmentIndex + 1];
          segmentTracks.segments.splice(segmentIndex + 1, 0, newSegment);
          if (isSegmentTransitionsEnabled && nextSegment) {
            removeSegmentTransition(newClip, afterSegmentId, nextSegment.id);
          }
        } else {
          segmentTracks.segments.push(newSegment);
        }
      }
      eventsToDispatch['SPLIT'] = { splitSource: 'ADD_VIDEO_SEGMENT' };

      break;
    }
    case CLIP_UPDATE_TYPE.REMOVE_SEGMENT_TRACK: {
      const { id: segmentId } = updateClipPayload;
      const playerConfig = newClip.asset_metadata.player_config;
      const segmentTracks = playerConfig?.playerConfig.tracks.find(
        track => track.type === TrackType.SEGMENTS
      ) as SegmentsTrack;

      if (!segmentTracks) {
        break;
      }

      const segmentToRemove = segmentTracks.segments.find(segment => segment.id === segmentId);

      segmentTracks.segments = segmentTracks.segments.filter(segment => segment.id !== segmentId);

      if (isSegmentTransitionsEnabled) {
        if (segmentId === 'intro') {
          updateTransitionsWhenSegmentRemoved(newClip, 'fromSegmentId', 'intro');
        } else if (segmentId === 'outro') {
          updateTransitionsWhenSegmentRemoved(newClip, 'toSegmentId', 'outro');
        } else {
          removeTransitionsForSegment(newClip, segmentId);
        }
      }

      eventsToDispatch['SPLIT'] = { splitSource: 'REMOVE_VIDEO_SEGMENT', type: segmentToRemove?.type };
      break;
    }
    case CLIP_UPDATE_TYPE.REORDER_SEGMENTS: {
      const { activeId, overId } = updateClipPayload;
      reorderSegment({
        newClip,
        activeId,
        overId
      });

      if (isSegmentTransitionsEnabled) {
        removeTransitionsForSegment(newClip, activeId);
      }

      break;
    }
    case CLIP_UPDATE_TYPE.CREATE_HOOK: {
      const { startTime, endTime, segmentId } = updateClipPayload;
      const { isolatedSelectionSegment, playerStartTime, playerEndTime } = isolateHookSelectionSegment({
        baseClip: oldClip,
        startTime,
        endTime,
        segmentId
      });

      const hookSegment = structuredClone(isolatedSelectionSegment);
      hookSegment.id = uuidv4();

      const playerConfig = newClip.asset_metadata.player_config;
      const segmentTracks = playerConfig?.playerConfig.tracks.find(
        track => track.type === TrackType.SEGMENTS
      ) as SegmentsTrack;
      if (!segmentTracks?.segments) {
        throw new Error('Segment tracks not found');
      }
      segmentTracks.segments.unshift(hookSegment);

      const mergedElementTracks = buildHookElementTracks({
        clip: oldClip,
        playerStartTime,
        playerEndTime
      });

      if (playerConfig) {
        const nonElementTracks = playerConfig.playerConfig.tracks.filter(t => t.type !== TrackType.ELEMENTS);
        playerConfig.playerConfig.tracks = [...nonElementTracks, ...mergedElementTracks];
      }
      break;
    }
    case CLIP_UPDATE_TYPE.REPLACE_ELEMENT_TRACKS: {
      const { elementTracks } = updateClipPayload;
      const playerConfig = newClip.asset_metadata.player_config;
      if (!playerConfig) {
        window.logger?.warn('REPLACE_ELEMENT_TRACKS: playerConfig is nullish, skipping element tracks update');
        break;
      }
      const nonElementTracks = playerConfig.playerConfig.tracks.filter(t => t.type !== TrackType.ELEMENTS);
      playerConfig.playerConfig.tracks = [...nonElementTracks, ...elementTracks];
      break;
    }
    case CLIP_UPDATE_TYPE.UPSERT_SEGMENT_TRANSITION_OUT: {
      const { transition } = updateClipPayload;
      upsertSegmentTransition(newClip, transition);
      break;
    }
    case CLIP_UPDATE_TYPE.REMOVE_SEGMENT_TRANSITION_OUT: {
      const { fromSegmentId, toSegmentId } = updateClipPayload;
      removeSegmentTransition(newClip, fromSegmentId, toSegmentId);
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_SEGMENT_TRACK: {
      const { startTime, endTime, type, duration, compositeId } = updateClipPayload;

      if (type !== SegmentType.COMPOSITE) {
        throw new Error('Not implemented');
      }

      const compositeSegment = getCompositeSegmentById(newClip, compositeId);
      if (!compositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }
      if (startTime !== undefined) {
        const boundedStartTime = Math.max(0, startTime);
        compositeSegment.segment_metadata.start = boundedStartTime;
        const newDeletes = getNewTrimFromStartDeletesForComposite(boundedStartTime, compositeSegment);
        if (newDeletes) {
          compositeSegment.segment_metadata.deletes = newDeletes;
        }
      }
      const maxVideoDuration = duration || compositeSegment.segment_metadata.maxVideoDuration;
      if (endTime !== undefined && maxVideoDuration) {
        const boundedEndTime = Math.min(endTime, maxVideoDuration);
        compositeSegment.segment_metadata.end = boundedEndTime;
        const newDeletes = getNewTrimFromEndDeletesForComposite(boundedEndTime, compositeSegment);
        if (newDeletes) {
          compositeSegment.segment_metadata.deletes = newDeletes;
        }
      }
      if (approxEquals(compositeSegment.segment_metadata.start, compositeSegment.segment_metadata.end)) {
        const allSegments = getAllSegments(newClip);
        const compositeSegmentCount = allSegments.filter(segment => segment.type === SegmentType.COMPOSITE).length;
        if (compositeSegmentCount === 1) {
          throw new Error('Cannot reduce the duration to 0');
        }
        const playerConfig = newClip.asset_metadata.player_config?.playerConfig;
        if (playerConfig) {
          const segmentTrack = playerConfig.tracks.find(track => track.type === TrackType.SEGMENTS) as SegmentsTrack;
          if (segmentTrack) {
            segmentTrack.segments.splice(
              allSegments.findIndex(segment => segment.id === compositeId),
              1
            );
          }
        }
      }
      eventsToDispatch['FULL_RECORDING_TRIM'] = true;
      eventsToDispatch['DURATION_CHANGED'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_VIDEO_SEGMENT_TRACK: {
      const { segmentId, startTime, endTime } = updateClipPayload;
      const allSegments = getAllSegments(newClip);
      const videoSegment = allSegments.find(s => s.id === segmentId);
      if (!videoSegment || videoSegment.type !== SegmentType.VIDEO || !videoSegment.segment_metadata) {
        throw new Error(`Video segment with id "${segmentId}" not found`);
      }

      if (startTime !== undefined) {
        const boundedStartTime = Math.max(0, startTime);
        videoSegment.segment_metadata.start = boundedStartTime;
        const newDeletes = getNewTrimFromStartDeletesForComposite(boundedStartTime, videoSegment);
        if (newDeletes) {
          videoSegment.segment_metadata.deletes = newDeletes;
        }
      }

      if (endTime !== undefined) {
        const maxDur = videoSegment.segment_metadata.maxVideoDuration;
        const boundedEndTime = Math.min(endTime, maxDur);
        videoSegment.segment_metadata.end = boundedEndTime;
        const newDeletes = getNewTrimFromEndDeletesForComposite(boundedEndTime, videoSegment);
        if (newDeletes) {
          videoSegment.segment_metadata.deletes = newDeletes;
        }
      }

      eventsToDispatch['VIDEO_SEGMENT_TRIM'] = true;
      eventsToDispatch['DURATION_CHANGED'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE: {
      addSegmentDeletes(newClip, updateClipPayload);
      eventsToDispatch['DURATION_CHANGED'] = true;
      eventsToDispatch['FULL_RECORDING_TRANSCRIPT_DELETE'] = true;

      break;
    }
    case CLIP_UPDATE_TYPE.BULK_ADD_COMPOSITE_SEGMENT_DELETES: {
      const { deletes, compositeId } = updateClipPayload;
      const compositeSegment = getCompositeSegmentById(newClip, compositeId);
      if (!compositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }
      compositeSegment.segment_metadata.deletes = mergeDeletes({
        ...compositeSegment.segment_metadata.deletes,
        ...deletes
      });
      eventsToDispatch['FULL_RECORDING_TRANSCRIPT_DELETE'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.BULK_PUT_COMPOSITE_SEGMENT_DELETE: {
      const { deletes, compositeId } = updateClipPayload;
      const compositeSegment = getCompositeSegmentById(newClip, compositeId);
      if (!compositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }
      compositeSegment.segment_metadata.deletes = mergeDeletes(deletes);
      eventsToDispatch['FULL_RECORDING_TRANSCRIPT_DELETE'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_COMPOSITE_SEGMENT_DELETE: {
      const { deleteId, endTime, startTime, compositeId } = updateClipPayload;
      const compositeSegment = getCompositeSegmentById(newClip, compositeId);
      if (!compositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }
      const newDeletes: ClipDeletes = structuredClone(compositeSegment.segment_metadata.deletes || {});
      delete newDeletes[deleteId];

      newDeletes[startTime] = {
        bounds: [startTime, endTime],
        prev_end_time: startTime,
        next_start_time: endTime
      };

      const mergeDeletesData = mergeDeletes(newDeletes);

      if (startTime <= compositeSegment.segment_metadata.start) {
        compositeSegment.segment_metadata.start = endTime;
      }
      if (endTime >= compositeSegment.segment_metadata.end) {
        compositeSegment.segment_metadata.end = startTime;
      }

      compositeSegment.segment_metadata.deletes = mergeDeletesData;

      break;
    }
    case CLIP_UPDATE_TYPE.REMOVE_COMPOSITE_SEGMENT_DELETE: {
      const { deleteId, compositeId } = updateClipPayload;
      const compositeSegment = getCompositeSegmentById(newClip, compositeId);
      if (!compositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }

      const deletes = structuredClone(compositeSegment.segment_metadata.deletes || {});
      delete deletes[deleteId];

      compositeSegment.segment_metadata.deletes = deletes;

      break;
    }
    case CLIP_UPDATE_TYPE.RESTORE_COMPOSITE_SEGMENT_DELETE: {
      addSegmentRestore(newClip, updateClipPayload);
      break;
    }
    case CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE_WORDS: {
      const { compositeId } = updateClipPayload;
      const oldCompositeSegment = getCompositeSegmentById(oldClip, compositeId);
      if (!oldCompositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }
      const newCompositeSegment = getCompositeSegmentById(newClip, compositeId);
      if (!newCompositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }
      addCompositeSegmentDelete(newCompositeSegment, updateClipPayload);

      const oldStart = roundToNDecimalPlaces(oldCompositeSegment.segment_metadata.start, 3);
      const oldEnd = roundToNDecimalPlaces(oldCompositeSegment.segment_metadata.end, 3);
      const newStart = roundToNDecimalPlaces(newCompositeSegment.segment_metadata.start, 3);
      const newEnd = roundToNDecimalPlaces(newCompositeSegment.segment_metadata.end, 3);

      if (newStart !== oldStart || newEnd !== oldEnd) {
        eventsToDispatch['DURATION_CHANGED'] = true;
      }

      eventsToDispatch['DELETE_WORDS'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_SCENE_CROP_POSITION: {
      const { sceneId, videoId, cropPosition, compositeId } = updateClipPayload;
      const compositeSegment = getCompositeSegmentById(newClip, compositeId);
      if (!compositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }
      if (sceneId && videoId) {
        const selectedScene = compositeSegment.segment_metadata.scenes.find(scene => scene.id === sceneId);
        if (selectedScene) {
          selectedScene.details.video_crop_position_map = {
            ...(selectedScene.details.video_crop_position_map || {}),
            [videoId]: cropPosition
          };
        }
      }
      break;
    }
    case CLIP_UPDATE_TYPE.ADD_COMPOSITE_SCENE: {
      const { currentFrame, compositeId } = updateClipPayload;
      const compositeSegment = getCompositeSegmentById(newClip, compositeId);
      if (!compositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }
      const scenes = compositeSegment.segment_metadata.scenes || [];

      let updatedScenes: Scene[] = [];

      if (scenes.length === 0) {
        updatedScenes = createNewScene({ newClip, currentFrame, compositeId });
      } else {
        updatedScenes = addNewSceneAtFrame({ newClip, currentFrame, compositeId });
      }

      compositeSegment.segment_metadata.scenes = updatedScenes;
      eventsToDispatch['ADD_SCENE'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.ADD_COMPOSITE_SCENE_RANGE: {
      const { startFrame, endFrame, compositeId } = updateClipPayload;
      const compositeSegment = getCompositeSegmentById(newClip, compositeId);
      if (!compositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }

      let updatedScenes: Scene[] = [];
      const scenes = compositeSegment.segment_metadata.scenes || [];

      if (scenes.length === 0) {
        updatedScenes = createNewSceneAtRange({
          newClip,
          startTimeFrame: startFrame,
          endTimeFrame: endFrame,
          compositeId
        });
      } else {
        updatedScenes = addNewSceneAtRange({
          newClip,
          startTimeFrame: startFrame,
          endTimeFrame: endFrame,
          compositeId
        });
      }
      compositeSegment.segment_metadata.scenes = updatedScenes;
      eventsToDispatch['ADD_SCENE'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.REMOVE_COMPOSITE_SCENE: {
      const { sceneId, compositeId } = updateClipPayload;
      const compositeSegment = getCompositeSegmentById(newClip, compositeId);
      if (!compositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }
      const updatedScenes = deleteScene({ newClip, compositeId, sceneId });

      compositeSegment.segment_metadata.scenes = updatedScenes;
      eventsToDispatch['DELETE_SCENE'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_COMPOSITE_SCENE: {
      const { sceneId, fromFrame, toFrame, layout, fitMode, compositeId } = updateClipPayload;
      const compositeSegment = getCompositeSegmentById(newClip, compositeId);
      if (!compositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }
      const scenes = compositeSegment.segment_metadata.scenes || [];
      const sceneIndex = scenes.findIndex(scene => scene.id === sceneId);

      if (sceneIndex === -1) {
        throw new Error('Scene not found');
      }

      const updatedScenes = [...scenes];
      const scene = updatedScenes[sceneIndex];

      if (fromFrame !== undefined) {
        scene.fromFrame = fromFrame;
        const previousScene = updatedScenes[sceneIndex - 1];
        if (previousScene) {
          previousScene.toFrame = fromFrame;
        }
      }
      if (toFrame !== undefined) {
        scene.toFrame = toFrame;
        const nextScene = updatedScenes[sceneIndex + 1];
        if (nextScene) {
          nextScene.fromFrame = toFrame;
        }
      }
      if (layout) {
        scene.details.layout = layout;
      }
      if (fitMode) {
        scene.details.fitMode = fitMode;
      }

      compositeSegment.segment_metadata.scenes = updatedScenes;
      if (updatedScenes.length === 1) {
        newClip.asset_metadata.layout = scene.details.layout;
        newClip.asset_metadata.fit = scene.details.fitMode;
      }
      eventsToDispatch['UPDATE_SCENE'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.TOGGLE_VIDEO_VISIBILITY_FROM_SCENE: {
      const { sceneId, videoId, compositeId, trackName } = updateClipPayload;
      const compositeSegment = getCompositeSegmentById(newClip, compositeId);
      if (!compositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }
      const updatedScenes = toggleVideoVisibilityFromScene({ newClip, compositeId, sceneId, videoId, trackName });
      newClip.asset_metadata.scenes = updatedScenes;
      eventsToDispatch['UPDATE_SCENE'] = true;
      break;
    }

    case CLIP_UPDATE_TYPE.UPDATE_CLIP_FIT_MODE: {
      const { fitMode } = updateClipPayload;
      const allCompositeSegments = getAllCompositeSegments(newClip);
      allCompositeSegments.forEach(compositeSegment => {
        if (!compositeSegment.segment_metadata) {
          throw new Error('Composite segment metadata not found');
        }
        compositeSegment.segment_metadata.fitMode = fitMode;
        const scenes = compositeSegment.segment_metadata.scenes || [];
        scenes.forEach(scene => {
          scene.details.fitMode = fitMode;
        });
        compositeSegment.segment_metadata.scenes = scenes;
      });

      newClip.asset_metadata.fit = fitMode;
      eventsToDispatch['UPDATE_SCENE'] = true;

      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CLIP_BORDERS_VISIBILITY: {
      const { hideBorders } = updateClipPayload;
      newClip.asset_metadata.hide_borders = hideBorders;
      eventsToDispatch[hideBorders ? 'HIDE_BORDERS' : 'SHOW_BORDERS'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CLIP_SIZE: {
      const { size } = updateClipPayload;
      newClip.asset_metadata.size = size;
      eventsToDispatch['CHANGE_SIZE'] = true;
      break;
    }
    // !!Deprecate after susnset of older players
    case CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT: {
      const { layout } = updateClipPayload;

      const layoutStatus = getLayoutAvailabilityStatus(layout, oldClip.layout_status, true);

      let updatedMetadata: Partial<Clip['asset_metadata']> = {
        layout: layout,
        size: oldClip.asset_metadata.size,
        isLayoutProcessing: layoutStatus === 'PROCESSING'
      };

      const isScaleDefined = isNumber(oldClip.asset_metadata.caption_styles?.scale);

      const defaultLineConfig = CaptionLineConfig.OneLine;

      // ! TODO @AshwinBhatkal isScaleDefined condition to be moved to updateClipResponse in clip.ts
      if (!isScaleDefined) {
        updatedMetadata = {
          ...updatedMetadata,
          caption_styles: {
            ...oldClip.asset_metadata.caption_styles,
            font_size: DEFAULT_FONT_SIZE,
            caps: oldClip.asset_metadata.caption_styles?.caps || false,
            animation: oldClip.asset_metadata.caption_styles?.animation || CaptionStyle.Outline,
            scale: 1,
            line_config: oldClip.asset_metadata.caption_styles?.line_config || defaultLineConfig
          }
        };
      }

      newClip.asset_metadata = {
        ...newClip.asset_metadata,
        ...updatedMetadata
      };
      eventsToDispatch['SELECT_MAGIC_LAYOUT'] = true;

      const allCompositeSegments = getAllCompositeSegments(newClip);
      allCompositeSegments.forEach(compositeSegment => {
        if (!compositeSegment.segment_metadata) {
          throw new Error('Composite segment metadata not found');
        }
        if (layout === 'AUDIOGRAM' && compositeSegment.scenes.length > 1) {
          compositeSegment.segment_metadata.scenes = [
            getDefaultSceneData({ clip: newClip, compositeId: compositeSegment.id })
          ];
        } else {
          const scenes = compositeSegment.segment_metadata.scenes || [];
          scenes.forEach(scene => {
            scene.details.layout = layout;
          });
          compositeSegment.segment_metadata.scenes = scenes;
        }
      });
      newClip.asset_metadata.layout = layout;
      eventsToDispatch['UPDATE_SCENE'] = true;

      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CLIP_SPEAKER_LABELS: {
      const { showSpeakerLabels } = updateClipPayload;

      if (newClip.asset_metadata.magicLayout) {
        newClip.asset_metadata.magicLayout = {
          ...(newClip.asset_metadata.magicLayout || {}),
          showSpeakerLabels
        };
        eventsToDispatch['TOGGLE_SPEAKER_LABELS'] = true;
        eventsToDispatch['UPDATE_MAGIC_LAYOUT'] = true;
      }
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CLIP_SPEAKER_LABEL_STYLES: {
      const { speakerLabelsStyles } = updateClipPayload;
      const currentStyles = oldClip.asset_metadata.speaker_label_styles || {};
      const finalSpeakerLabelsStyles = {
        ...currentStyles,
        ...speakerLabelsStyles,
        containerStyle: {
          ...(currentStyles.containerStyle || {}),
          ...(speakerLabelsStyles.containerStyle || {})
        },
        nameStyle: {
          ...(currentStyles.nameStyle || {}),
          ...(speakerLabelsStyles.nameStyle || {})
        },
        titleStyle: {
          ...(currentStyles.titleStyle || {}),
          ...(speakerLabelsStyles.titleStyle || {})
        },
        transitions: currentStyles.transitions
          ? {
              ...currentStyles.transitions,
              ...(speakerLabelsStyles.transitions || {})
            }
          : speakerLabelsStyles.transitions
      };

      newClip.asset_metadata.speaker_label_styles = finalSpeakerLabelsStyles;
      eventsToDispatch['UPDATE_SPEAKER_LABEL_STYLES'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_WORD_HIGHLIGHT: {
      const { updatePayload } = updateClipPayload;
      const clipMetadata = oldClip.asset_metadata;

      const hasHighlightType = Object.keys(updateClipPayload).includes('highlightType');
      const hasHighlightColor = Object.keys(updateClipPayload).includes('highlightColor');
      const hasTextHighlightColor = Object.keys(updateClipPayload).includes('textHighlightColor');

      const { highlightType, highlightColor, textHighlightColor } = updatePayload;
      if (hasHighlightType) {
        newClip.asset_metadata.highlight_type = highlightType;
        newClip.asset_metadata.word_highlight_color =
          highlightType === 'box' ? clipMetadata.word_highlight_color || '#000000' : clipMetadata.word_highlight_color;
        newClip.asset_metadata.text_highlight_color =
          highlightType !== 'none'
            ? clipMetadata.text_highlight_color || clipMetadata.magicLayout?.textColor || '#ffffff'
            : clipMetadata.text_highlight_color;
        eventsToDispatch['UPDATE_HIGHLIGHT_TYPE'] = true;
      }
      if (hasHighlightColor) {
        newClip.asset_metadata.word_highlight_color = highlightColor;
        eventsToDispatch['UPDATE_HIGHLIGHT_COLOR'] = true;
      }
      if (hasTextHighlightColor) {
        newClip.asset_metadata.text_highlight_color = textHighlightColor;
        eventsToDispatch['UPDATE_TEXT_HIGHLIGHT_COLOR'] = true;
      }
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CAPTIONS_POSITION: {
      const { captionPositions } = updateClipPayload;
      newClip.asset_metadata.caption_positions = captionPositions;
      eventsToDispatch['CHANGE_CAPTIONS_POSITION'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CAPTION_STYLES: {
      const { captionStyles } = updateClipPayload;
      if (newClip.asset_metadata.caption_styles) {
        newClip.asset_metadata.caption_styles = {
          ...(newClip.asset_metadata.caption_styles || {}),
          ...captionStyles
        };
      } else {
        newClip.asset_metadata.caption_styles = {
          ...getDefaultCaptionStyles(newClip),
          // !! Hitesh need to confirm with kashis the reason for this value.
          animation: CaptionStyle.Basic,
          ...captionStyles
        };
      }
      eventsToDispatch['CAPTION_STYLES_V2'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.TOGGLE_CAPTIONS: {
      const { enabled } = updateClipPayload;

      newClip.asset_metadata.subtitle = enabled;
      newClip.asset_metadata.caption_styles = getDefaultCaptionStyles(newClip);
      eventsToDispatch[enabled ? 'CAPTIONS_ON' : 'CAPTIONS_OFF'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CAPTION_PRESET: {
      const { presetName } = updateClipPayload;
      newClip.asset_metadata.caption_styles = {
        ...getDefaultCaptionStyles(newClip),
        ...(newClip.asset_metadata.caption_styles || {}),
        ...getCaptionPreset(presetName)
      };
      newClip.asset_metadata.subtitle = true;
      eventsToDispatch['UPDATE_CAPTION_PRESET'] = { presetName };
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CLIP_FONT_LOCATION: {
      const { path } = updateClipPayload;
      newClip.asset_metadata.font_location = path;
      newClip.asset_metadata.caption_styles = {
        ...getDefaultCaptionStyles(newClip),
        ...(newClip.asset_metadata.caption_styles || {}),
        font_location: path
      };
      eventsToDispatch['UPDATE_FONT_LOCATION'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_MAGIC_LAYOUT: {
      const { updatePayload } = updateClipPayload;
      if (newClip.asset_metadata.magicLayout) {
        newClip.asset_metadata.magicLayout = {
          ...newClip.asset_metadata.magicLayout,
          ...updatePayload
        };
      }
      eventsToDispatch['UPDATE_MAGIC_LAYOUT'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_VISIBLE_FACE_IDS: {
      const { visibleFaceIds } = updateClipPayload;
      newClip.asset_metadata.visible_face_ids = visibleFaceIds;
      eventsToDispatch['UPDATE_VISIBLE_FACE_IDS'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_VISIBLE_SPEAKERS: {
      const { visibleSpeakers } = updateClipPayload;
      newClip.asset_metadata.visible_speakers = visibleSpeakers;
      eventsToDispatch['UPDATE_VISIBLE_SPEAKERS'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_VISIBLE_VIDEO_IDS: {
      const { visibleVideoIds } = updateClipPayload;
      newClip.asset_metadata.visible_video_ids = visibleVideoIds;
      eventsToDispatch['UPDATE_VISIBLE_VIDEO_IDS'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_EFFECT: {
      const { effect, compositeId } = updateClipPayload;

      const playerConfig = newClip.asset_metadata.player_config;
      if (playerConfig) {
        const compositeSegment = getCompositeSegmentById(newClip, compositeId);
        if (compositeSegment) {
          compositeSegment.effects = [...(compositeSegment.effects || []), effect].sort((a, b) => a.start - b.start);
        }
      }
      eventsToDispatch['ADD_COMPOSITE_SEGMENT_EFFECT'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.EDIT_WORD: {
      const { words, newValue, compositeId } = updateClipPayload;
      const compositeSegment = getCompositeSegmentById(newClip, compositeId);
      if (!compositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }
      applyWordEdit(compositeSegment, words, newValue);
      eventsToDispatch['EDIT_WORD'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.HIDE_WORD: {
      const { words, compositeId } = updateClipPayload;
      const compositeSegment = getCompositeSegmentById(newClip, compositeId);
      if (!compositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }

      applyWordHide(compositeSegment, words);
      eventsToDispatch['HIDE_WORD'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.SHOW_WORD: {
      const { words, compositeId } = updateClipPayload;
      const compositeSegment = getCompositeSegmentById(newClip, compositeId);
      if (!compositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }
      applyWordShow(compositeSegment, words);
      eventsToDispatch['SHOW_WORD'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.CORRECT_ALL: {
      const { paragraphs, editingContent, newValue, selectedWords } = updateClipPayload;
      const allCompositeSegments = getAllCompositeSegments(newClip);
      allCompositeSegments.forEach(compositeSegment => {
        if (!compositeSegment.segment_metadata) {
          throw new Error('Composite segment metadata not found');
        }
        applyCorrectAll(compositeSegment, paragraphs, editingContent, newValue, selectedWords);
      });
      eventsToDispatch['EDIT_WORD'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.SPLIT_SEGMENT: {
      const { startTime } = updateClipPayload;
      const segmentAtSplitTime = getSegmentAtCurrentFrame(startTime, newClip);
      if (segmentAtSplitTime?.type === SegmentType.COMPOSITE) {
        eventsToDispatch['SPLIT'] = { splitSource: 'SPLIT_COMPOSITE' };
      }
      splitSegment(newClip, startTime);
      break;
    }
    case CLIP_UPDATE_TYPE.UPSERT_ELEMENT_TRANSITION: {
      const { elementId, transitionLifeCycle, updateLifeCycleTransition } = updateClipPayload;
      const playerConfig = getClipReadOnlyPlayerConfig(newClip);
      if (!playerConfig) {
        throw new Error('Player config not found');
      }
      let element = getPlayerConfigElementById(playerConfig, elementId) as ImageElement | VideoElement;
      if (!element) {
        throw new Error('Element not found');
      }
      element = updateElementTransition({
        element,
        lifeCycleType: transitionLifeCycle,
        lifeCycleTransition: updateLifeCycleTransition,
        fps: getClipFPS(newClip)
      });
      const updatedPlayerConfig = findAndReplaceElementTrackItemInPlayerConfig(playerConfig, elementId, element);
      newClip.asset_metadata.player_config = updatedPlayerConfig;
      break;
    }

    case CLIP_UPDATE_TYPE.BULK_UPDATE_COMPOSITE_SEGMENT_SCENES: {
      const { compositeScenes } = updateClipPayload;
      for (const { compositeId, scenes } of compositeScenes) {
        const compositeSegment = getCompositeSegmentById(newClip, compositeId);
        if (!compositeSegment.segment_metadata) {
          throw new Error('Composite segment metadata not found');
        }
        compositeSegment.segment_metadata.scenes = scenes;
      }
      break;
    }
    case CLIP_UPDATE_TYPE.BULK_UPDATE_COMPOSITE_SEGMENT_EFFECTS: {
      const { compositeEffects } = updateClipPayload;
      for (const { compositeId, effects } of compositeEffects) {
        const compositeSegment = getCompositeSegmentById(newClip, compositeId);
        compositeSegment.effects = effects;
      }
      break;
    }
    case CLIP_UPDATE_TYPE.BULK_UPDATE_COMPOSITE_SEGMENT_DELETES: {
      const { compositeDeletes } = updateClipPayload;
      if (!compositeDeletes.length) {
        break;
      }
      for (const { compositeId, trims } of compositeDeletes) {
        const compositeSegment = getCompositeSegmentById(newClip, compositeId);
        if (!compositeSegment.segment_metadata) {
          throw new Error('Composite segment metadata not found');
        }
        const newDeletes: ClipDeletes = trims.reduce((acc, trim) => {
          acc[trim.prev_end_time] = trim;
          return acc;
        }, {} as ClipDeletes);

        compositeSegment.segment_metadata.deletes = mergeDeletes({
          ...compositeSegment.segment_metadata.deletes,
          ...newDeletes
        });
        for (const trim of trims) {
          const [trimStart, trimEnd] = trim.bounds;
          if (
            trimStart <= compositeSegment.segment_metadata.start &&
            trimEnd > compositeSegment.segment_metadata.start
          ) {
            compositeSegment.segment_metadata.start = trimEnd;
          }
          if (trimEnd >= compositeSegment.segment_metadata.end && trimStart < compositeSegment.segment_metadata.end) {
            compositeSegment.segment_metadata.end = trimStart;
          }
        }
      }
      eventsToDispatch['DURATION_CHANGED'] = true;
      eventsToDispatch['FULL_RECORDING_TRANSCRIPT_DELETE'] = true;
      break;
    }
    default: {
      throw new Error('Not implemented');
    }
  }

  if (skipPostProcessing) {
    return newClip;
  }

  updateCaptions(newClip);
  updateGlobalCaptions(newClip);
  trimScenes(newClip);
  updateCaptionPositions(newClip);

  // Required to copy all segment_metadata to asset_metadata when splits check is disabled after operation
  const newPlayerConfig = getPlayerConfig({ clip: newClip, shouldUpdatePlayerConfig: true });
  const splitsNotEnabledAfterOperation = !isSplitsInTimelineEnabled(newClip);
  if (splitsNotEnabledAfterOperation) {
    injectSegmentMetaDataInClip(newClip);
  }

  newClip.asset_metadata.player_config = newPlayerConfig;
  newClip.asset_metadata.remotionConfig = null;

  if (!splitsNotEnabledAfterOperation) {
    syncAssetMetadataFromSegments(newClip);
  }

  const version = getClipVersion();
  newClip.version = version;

  if (skipStoreAndBackendUpdate) {
    return newClip;
  }

  // dispatch events
  Object.keys(eventsToDispatch).forEach(event => {
    const eventPayload: any[] = [clipId, event];
    if (typeof eventsToDispatch[event] === 'object') {
      eventPayload.push(eventsToDispatch[event]);
    }
    trackClipUpdate(...(eventPayload as [string, string, Record<string, any> | undefined]));
  });

  currentClip.update(data => {
    data[clipId] = newClip;
    return data;
  });

  allClips.update(allClipsData => allClipsData.map(clip => (clip.id === newClip.id ? newClip : clip)));

  // When are are not updating the backend, we can don't want to generate thumbnail or add to history.
  if (skipBackendUpdate) {
    return newClip;
  }

  if (shouldGenerateThumbnail(actionType)) {
    debouncedGenerateThumbnail(clipId);
  }

  if (isChapterAffectingAction(actionType)) {
    // This flow is used since chapters are not a part of clip state and neither directly accessible via store.
    // Chapter component listens to this event and makes necessary adjustments to chapter timings if needed.
    EventBus.dispatch(CustomEvents.ChapterAdjustmentNeeded, {
      actionType,
      updateClipPayload,
      oldClip: oldClip,
      newClip: newClip
    });
  }

  if (addToHistory) {
    updateHistory(clipId, newClip.asset_metadata);
  }

  // after save call change updated_at flag.

  // TODO recheck the logic
  const isTranslatedCaptionEdited =
    actionForTranslatedCaptionChange.includes(actionType) &&
    oldClip.asset_metadata.original_lang &&
    oldClip.asset_metadata.selected_lang &&
    oldClip.asset_metadata.original_lang !== oldClip.asset_metadata.selected_lang;

  const onClipUpdateSuccess = (updatedClip: Clip) => {
    templateLoader.set(() => false);
    updateCurrentClipUpdatedAt(updatedClip);
  };

  if (!skipBackendUpdate) {
    if (isTranslatedCaptionEdited) {
      debouncedUpdateTranslatedCaption({
        clip: currentClip.getSnapshot()[clipId],
        onSuccess: onClipUpdateSuccess,
        version
      });
    } else {
      debouncedUpdateClipApi({
        clip: currentClip.getSnapshot()[clipId],
        onSuccess: onClipUpdateSuccess,
        version
      });
    }
  }

  return newClip;

  //  on failure errors
};
